import { PropostaVersao } from 'models/PropostaVersao';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Row,
  Table,
} from 'reactstrap';
import MostrarValor from 'views/components/MostrarValor';
import { PropostaVersaoParcelas } from 'models/PropostaVersaoParcelas';
import formatCurrency from 'utils/formatCurrency';
import { arredondaDecimais } from 'utils/arredondaDecimais';
import {
  FaExclamationTriangle,
  FaInfoCircle,
  FaPlusCircle,
  FaTrash,
  FaMinusCircle,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import Subtitle from 'views/components/Subtitle';
import {
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import api from 'services/api';
import { PropostaPrecificacaoInfo } from 'models/PropostaPrecificacaoInfo';
import { PropostaConfiguracao } from 'models/PropostaConfiguracao';
import toast from 'react-hot-toast';
import { Label } from '../../../Resumo/style';
import { Td, Th } from '../../styles';
import { iAcessoPrograma, iPropostaDados } from '../..';
import './style.css';

interface iPropostaVersaoParcelas extends PropostaVersaoParcelas {
  dias?: number;
  calculo?: number;
}

interface iDadosTabulados {
  id: string;
  tipo: string;
  configuracaoId?: number | null;
  orcId: number | null;
  tipoImportacaoId?: number | null;
  titulo: string;
  config: string;
  valorTotal: number;
  valorFinalComOutros: number;
  valorTotalOutros: number;
  cotacaoDolar: number;
  valorSeguro: number;
  valorOutros: number;
  custoItens: number;
  desconto: number;
  percentualICMS: number;
  percentualIPI: number;
  percentualPIS: number;
  percentualProposta: number;
  percentualST: number;
  somaPercentuais: number;
  valorDesconto: number;
  valorFaturamentoDireto: number;
  valorDolar: number;
  percentualFrete: number;
  divisaoProduto: string;
  valorItens: number;
  valorDespachante: number;
  motivoOutros: string;
  valoresModificados: boolean | false;
  isAtingirMeta: boolean | false;
  valorFinalComDesconto: number;
  valorFinalSemDesconto: number;
  valorFrete: number;
  valorProporcional: number;
  valorSemArr: number;
  valorSemArrSemDesconto: number;
  valorServico: number;
  valorTotalSemArr: number;
  valorFinal: number;
}

export default function Resumo({
  propostaVersao,
  isModoSimulacao,
  onChange,
  acessoPrograma,
  erroPosSalvar,
}: {
  propostaVersao: iPropostaDados;
  isModoSimulacao: boolean;
  acessoPrograma: iAcessoPrograma;
  onChange: (a: iPropostaDados) => void;
  erroPosSalvar: boolean;
}): JSX.Element {
  const dataEmbarque = new Date();

  const [isLoading, setIsLoading] = useState(false);
  const [formModalReportState, setFormModalReportState] = useState(false);
  const [dataList, setDataList] = useState<iDadosTabulados[]>([]);
  const [dadosOriginal, setDadosOriginal] = useState<iDadosTabulados[]>([]);

  const [isObraExterior, setIsObraExterior] = useState(
    propostaVersao.isExterior ?? false,
  );

  const [dataUpdate, setDataUpdate] = useState<iDadosTabulados>();
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  async function montarDados() {
    if (!propostaVersao) return;

    const itensPrecificacao = propostaVersao.precificacao as iDadosTabulados[];

    if (itensPrecificacao && itensPrecificacao.length > 0) {
      setDataList([...itensPrecificacao]);
      // if (dadosOriginal.length === 0) {
      if (!isModoSimulacao) {
        setDadosOriginal([...itensPrecificacao]);
      }
    }
  }

  useEffect(() => {
    montarDados();
  }, [propostaVersao]);

  function formatarMoeda(valor: number): string {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  function handleChange(id: string, name: string, valor: number | string) {
    const registro = dataList.find(item => item.id === id);

    let novaLista = dataList;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const registroValor = registro[name];

    const newRegistro = {
      ...registro,
      valorFinalComOutros: (registro?.valorFinalComOutros ?? 0) - registroValor,
    };

    if (registro) {
      novaLista = dataList.map(item => {
        if (item.id === id) {
          const atualizacao: any = {
            ...item,
            [name]: valor,
          };

          // Quando a mudança for no motivo, não é necessário atualizar o valorFinalComOutros
          if (typeof registroValor === 'number') {
            atualizacao.valorFinalComOutros =
              Number(newRegistro.valorFinalComOutros) + Number(valor);
          }

          return atualizacao;
        }

        return item;
      });
    }

    setDataList(novaLista);

    onChange({
      ...propostaVersao,
      precificacao: novaLista,
    });
  }

  function toggleRow(id: string) {
    setExpandedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
    );
  }

  // Expand/collapse all logic
  const allIds = dataList.map(item => item.id);
  const allExpanded =
    allIds.length > 0 && expandedRows.length === allIds.length;
  function handleToggleAll() {
    if (allExpanded) {
      setExpandedRows([]);
    } else {
      setExpandedRows(allIds);
    }
  }

  function renderContent(
    tipo: string,
    config: iDadosTabulados,
  ): React.ReactNode[] {
    const isExpanded = expandedRows.includes(config.id);

    const rows = [
      <tr
        key={`${tipo}-${config.titulo}-${config.orcId}-${config.id}`}
        style={{ background: '#e5e7eb', borderBottom: '2px solid #b0b3b8' }}
      >
        <Td
          style={{
            position: 'relative',
            fontWeight: 600,
            fontSize: '1rem',
            fontFamily: 'inherit',
            textAlign: 'left',
            background: '#e5e7eb',
            borderRadius: '8px 0 0 8px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            borderBottom: '2px solid #b0b3b8',
            paddingTop: 16,
            paddingBottom: 16,
          }}
        >
          <IconButton
            size="small"
            onClick={() => toggleRow(config.id)}
            style={{ marginRight: 8 }}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </IconButton>
          {config.titulo} | Valor faturamento direto{' '}
          {config.valorFaturamentoDireto}
        </Td>
        <Td
          style={{
            textAlign: 'right',
            fontWeight: 600,
            fontSize: '1rem',
            fontFamily: 'inherit',
            position: 'relative',
            paddingRight: 32,
            background: '#e5e7eb',
            borderRadius: '0 8px 8px 0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            borderBottom: '2px solid #b0b3b8',
            paddingTop: 16,
            paddingBottom: 16,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            {formatarMoeda(config.valorFinalComOutros)}
            <Tooltip
              title={
                <ul className="p-2 mt-2" style={{ listStyleType: 'none' }}>
                  <li>
                    <Label>ICMS: {config.percentualICMS}%</Label>
                  </li>
                  <li>
                    <Label>IPI: {config.percentualIPI}%</Label>
                  </li>
                  <li>
                    <Label>PIS: {config.percentualPIS}%</Label>
                  </li>
                  <li>
                    <Label>ST: {config.percentualST}%</Label>
                  </li>
                  <li>
                    <Label>
                      Outros: R$ {formatCurrency(config.valorOutros)}
                    </Label>
                  </li>
                  <li hidden={!isObraExterior}>
                    <Label>
                      Despachante: R$ {formatCurrency(config.valorDespachante)}
                    </Label>
                  </li>
                  <li hidden={!isObraExterior}>
                    <Label>
                      Seguro: R$ {formatCurrency(config.valorSeguro)}
                    </Label>
                  </li>
                </ul>
              }
              placement="top"
              arrow
            >
              <IconButton style={{ marginLeft: 4, padding: 0 }} size="small">
                <FaInfoCircle size={14} color="#888787" />
              </IconButton>
            </Tooltip>
          </span>
        </Td>
      </tr>,
    ];

    if (isExpanded) {
      rows.push(
        <tr
          key={`details-${tipo}-${config.titulo}-${config.orcId}-${config.id}`}
        >
          <Td
            colSpan={2}
            style={{
              background: '#f3f4f6',
              borderRadius: 8,
              border: '1.5px solid #b0b3b8',
              padding: 24,
              paddingTop: 32,
              paddingBottom: 32,
            }}
          >
            <Row
              style={{
                rowGap: 24,
                marginTop: 20,
                marginBottom: 20,
                paddingLeft: 20,
              }}
            >
              <Col sm="4">
                <TextField
                  label={
                    <span
                      style={{
                        color: '#374151',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      Valor Outros (R$)
                    </span>
                  }
                  type="number"
                  className="mt-2"
                  value={config.valorOutros ?? 0}
                  onChange={e =>
                    handleChange(
                      config.id,
                      'valorOutros',
                      Number(e.target.value),
                    )
                  }
                  disabled={!isModoSimulacao}
                  InputProps={{ style: { padding: 10 } }}
                />
              </Col>
              <Col sm="4" hidden={!isObraExterior}>
                <TextField
                  label={
                    <span
                      style={{
                        color: '#374151',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      Valor Seguro (R$)
                    </span>
                  }
                  type="number"
                  className="mt-2"
                  value={config.valorSeguro ?? 0}
                  onChange={e =>
                    handleChange(
                      config.id,
                      'valorSeguro',
                      Number(e.target.value),
                    )
                  }
                  disabled={!isModoSimulacao}
                  InputProps={{ style: { padding: 10 } }}
                />
              </Col>
              <Col sm="4" hidden={!isObraExterior}>
                <TextField
                  label={
                    <span
                      style={{
                        color: '#374151',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      Valor Despachante (R$)
                    </span>
                  }
                  type="number"
                  className="mt-2"
                  value={config.valorDespachante ?? 0}
                  onChange={e =>
                    handleChange(
                      config.id,
                      'valorDespachante',
                      Number(e.target.value),
                    )
                  }
                  disabled={!isModoSimulacao}
                  InputProps={{ style: { padding: 10 } }}
                />
              </Col>
              <Col sm="6">
                <TextField
                  label={
                    <span
                      style={{
                        color: `${
                          config.valorOutros !== 0 &&
                          config.motivoOutros === '' &&
                          erroPosSalvar
                            ? 'red'
                            : '#374151'
                        }`,
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      Motivo Outros
                    </span>
                  }
                  type="text"
                  className="sm-8 mt-2 red"
                  value={config.motivoOutros ?? ''}
                  onChange={e =>
                    handleChange(config.id, 'motivoOutros', e.target.value)
                  }
                  disabled={!isModoSimulacao}
                  InputProps={{ style: { padding: 10, width: '100%' } }}
                  fullWidth
                  helperText={
                    config.valorOutros !== 0 &&
                    config.motivoOutros === '' &&
                    erroPosSalvar
                      ? 'Obrigatório.'
                      : ''
                  }
                  error={
                    config.valorOutros !== 0 &&
                    config.motivoOutros === '' &&
                    erroPosSalvar
                  }
                />
              </Col>
              <Col sm="4" hidden={!propostaVersao.outros?.isRegramentoGeral}>
                <TextField
                  label={
                    <span
                      style={{
                        color: '#374151',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      Valor Faturamento Direto (R$)
                    </span>
                  }
                  type="number"
                  className="mt-2"
                  value={config.valorFaturamentoDireto ?? 0}
                  onChange={e =>
                    handleChange(
                      config.id,
                      'valorFaturamentoDireto',
                      Number(e.target.value),
                    )
                  }
                  disabled={!isModoSimulacao}
                  InputProps={{ style: { padding: 10 } }}
                />
              </Col>
            </Row>
          </Td>
        </tr>,
      );
    }
    return rows;
  }

  // Calcular o valor final original da proposta e o atual
  const valorFinalOriginal = propostaVersao.valores.valorFinalComOutros ?? 0;
  const valorTotalOutros = propostaVersao.precificacao.reduce(
    (accumulator, currentValue) => accumulator + currentValue.valorOutros,
    0,
  );
  const valorFinalAtual = propostaVersao.precificacao.reduce(
    (accumulator, currentValue) =>
      accumulator + currentValue.valorFinalComOutros,
    0,
  );
  console.log({
    prec: propostaVersao.precificacao,
  });
  // const valorFinalAtual = dataList.reduce(
  //   (acc, curr) => acc + (curr.valorFinalComOutros ?? 0),
  //   0,
  // );

  // Definir a cor do texto "valor final" do equipamento: vermelho se mudou, padrão se não
  const corValorFinal =
    isModoSimulacao && valorFinalAtual !== valorFinalOriginal
      ? '#d32f2f' // vermelho
      : '#212529'; // cor padrão

  return (
    <div>
      <Card className="shadow p-3" style={{ position: 'relative' }}>
        {/* Expand/Collapse All Button */}
        <IconButton
          onClick={handleToggleAll}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
          }}
          size="small"
          aria-label={allExpanded ? 'Recolher todos' : 'Expandir Eequipamentos'}
        >
          <span
            style={{ marginRight: 6, fontWeight: 600, fontSize: '0.95rem' }}
          >
            Expandir Equipamentos
          </span>
          {allExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </IconButton>
        <Subtitle name="Resumo" />
        <CardBody>
          <div className="resumo-table-wrapper">
            <Table responsive bordered className="mt-2 resumo-table">
              <thead>
                <tr>
                  <Th
                    style={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                    }}
                  >
                    Equipamento
                  </Th>
                  <Th
                    style={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      textAlign: 'right',
                    }}
                  >
                    Valor
                  </Th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td />
                    <td className="text-center">
                      <CircularProgress
                        className="mt-2 ml-2"
                        color="inherit"
                        size={20}
                      />
                    </td>
                  </tr>
                )}
                {dataList && dataList.length > 0
                  ? dataList.flatMap(config =>
                      renderContent(config.tipo, config),
                    )
                  : null}
              </tbody>
              <tfoot>
                {isLoading ? (
                  ''
                ) : (
                  <>
                    <tr>
                      <td />
                      <td
                        style={{
                          textAlign: 'right',
                          fontWeight: 600,
                          fontSize: '1rem',
                          fontFamily: 'inherit',
                          paddingRight: 32,
                          color: corValorFinal,
                          transition: 'color 0.3s',
                        }}
                      >
                        Valor final: &nbsp;
                        {formatarMoeda(valorFinalAtual || 0)}
                      </td>
                    </tr>
                    <tr
                      hidden={
                        dataList.filter(item => item.valoresModificados)
                          ?.length === 0
                      }
                    >
                      <td />
                      <td style={{ textAlign: 'right' }}>
                        <Badge color="warning" className="badge-lg" pill>
                          Valor Novo:{' '}
                          {formatarMoeda(
                            propostaVersao.precificacao.reduce(
                              (accumulator, currentValue) =>
                                accumulator +
                                ((currentValue.valorFinal ?? 0) +
                                  // (currentValue.valorOutros ?? 0) +
                                  (currentValue.valorDespachante ?? 0) +
                                  (currentValue.valorSeguro ?? 0)),
                              0,
                            ) || 0,
                          )}
                        </Badge>
                      </td>
                    </tr>
                  </>
                )}
              </tfoot>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
